import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from './Button';
import { Icon } from './icon';

/**
 * V64: o motivo é obrigatório nos TRÊS tipos. Antes o comércio ficava de fora porque o DTO de
 * status não tinha campo e o texto era descartado; agora ele é persistido no log de moderação e o
 * backend recusa a chamada sem `reasonCode` (@AssertTrue no UpdateStatusRequest).
 */
const rejectReasonRequired = (_kind: 'user' | 'merchant' | 'courier'): boolean => true;

/**
 * Confirmation modal for **rejecting** a pending cadastro (the mockup has no
 * such screen — spec §2.1 introduces it as the reject gesture for both kinds).
 *
 * - `kind === 'user'` (comerciante): the rejection reason is **required**
 *   (backend errors `REJECTION_REASON_REQUIRED`), so **Recusar** stays disabled
 *   until a non-blank reason is typed.
 * - `kind === 'merchant'` (comércio): the status DTO carries no reason field, so
 *   the textarea is collected for the operator's benefit but **Recusar** is
 *   always enabled (`rejectReasonRequired` decides this).
 *
 * `loading` disables both actions during the in-flight mutation. Built on Radix
 * Dialog (Esc / overlay-click close, focus-trap); the portalled content carries
 * its own `.merchant-theme` so the CSS vars resolve outside the shell subtree.
 */
export function RejectModal({
  open,
  kind,
  name,
  loading = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  kind: 'user' | 'merchant' | 'courier' | null;
  name?: string;
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');

  // Reset the textarea whenever a fresh item opens the modal (or it closes), so
  // a previous item's reason never leaks into the next rejection.
  useEffect(() => {
    if (open) setReason('');
  }, [open, kind, name]);

  const reasonRequired = kind ? rejectReasonRequired(kind) : false;
  const confirmDisabled = loading || (reasonRequired && reason.trim().length === 0);

  const handleOpenChange = (next: boolean): void => {
    if (loading && !next) return; // block Esc / overlay close mid-flight
    if (!next) onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="merchant-theme fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className="merchant-theme fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[var(--m-r-lg)] bg-[var(--m-surface)] p-6 text-[var(--m-fg)] shadow-[var(--m-shadow-lg)] focus:outline-none data-[state=open]:animate-slide-up"
        >
          <div className="mb-4 flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--m-r-md)] bg-[var(--m-bg-alt)] text-[var(--m-brand-press)]">
              <Icon name="thumbs-down" size={20} />
            </span>
            <div className="min-w-0">
              <Dialog.Title className="text-[18px] font-extrabold tracking-[-0.01em] text-[var(--m-fg)]">
                Recusar cadastro
              </Dialog.Title>
              {name ? (
                <p className="mt-0.5 truncate text-[13px] text-[var(--m-fg-muted)]">{name}</p>
              ) : null}
            </div>
          </div>

          {/* V64: o motivo agora é PERSISTIDO para os 3 tipos (users.rejection_reason + o log de
              moderação append-only), e o backend recusa a chamada sem ele. */}
          {reasonRequired ? (
            <>
              {/* `htmlFor`/`id` de verdade: sem o par, o leitor de tela anuncia o
                  campo sem dizer o que ele quer, e tocar o rótulo não põe o foco no
                  textarea. Era o único ERRO de lint do workspace (pré-existente). */}
              <label
                htmlFor="reject-modal-reason"
                className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.04em] text-[var(--m-fg-muted)]"
              >
                Motivo da recusa
              </label>
              <textarea
                id="reject-modal-reason"
                className="min-h-[96px] w-full resize-y rounded-[var(--m-r-md)] border-[1.5px] border-[var(--m-border)] bg-[var(--m-surface)] px-3.5 py-3 text-[14px] text-[var(--m-fg)] outline-none placeholder:text-[var(--m-fg-subtle)] focus:border-[var(--m-brand)]"
                placeholder="Explique por que o cadastro foi recusado (o comerciante vê esse motivo)."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loading}
              />
            </>
          ) : (
            <p className="text-[14px] text-[var(--m-fg-muted)]">
              Esta ação recusa o cadastro. Só um administrador consegue reabrir depois.
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              icon="thumbs-down"
              onClick={() => onConfirm(reason.trim())}
              disabled={confirmDisabled}
              aria-busy={loading || undefined}
            >
              Recusar
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
